
import React, { useState, useEffect } from "react";
import { ComplianceCard as IComplianceCard, CoAssistSection, RejectionLogEntry } from "../../types/compliance";
import { FileUploadKit } from "../common/FileUploadKit";
import { useAuth } from "../../context/AuthContext";
import { isSuperUser, getDeptCodeByEmail } from "../../config/roles";
import { getDeptNameByCode } from "../../constants/departments";
import { updateComplianceCard, submitCardForApproval, archiveCard, moveIntelToProject, setRegIntelManualReview } from "../../services/complianceService";
import { uploadGovProjectFiles } from "../../services/storageUpload"; 
import { useToast } from "../Toast";
import { analyzeUploadedFile, callLegalAssistant } from "../../services/aiService";
import { DEPARTMENTS } from "../../constants/departments";
import { ApprovalFlowPanel } from "../approval/ApprovalFlowPanel";
import { useApprovalFlow } from "../../hooks/useApprovalFlow";
import { updateStageDecision } from "../../services/approvalFlowService";
import { AiFieldHeader } from "../shared/AiFieldHeader";
import { Modal } from "../Modal";
import type { AttachmentMeta } from "../../types/attachments";

interface Props {
  card: IComplianceCard;
  onRefresh: () => void;
  readOnly?: boolean;
}

export const ComplianceCard: React.FC<Props> = ({ card, onRefresh, readOnly }) => {
  const { user } = useAuth();
  const { showToast } = useToast();
  const isSuper = isSuperUser(user?.email);

  // User Identity
  const userDeptCode = getDeptCodeByEmail(user?.email);
  const userDeptName = getDeptNameByCode(userDeptCode);
  
  // Ownership Checks
  // Check if current user's dept code matches ownerDept (which might be code or name)
  // For safety, convert everything to Code for comparison
  const ownerDeptCode = DEPARTMENTS.find(d => d.name === card.sectionB.ownerDept || d.code === card.sectionB.ownerDept)?.code || card.sectionB.ownerDept;
  const isOwnerDept = ownerDeptCode === userDeptCode;
  const isOwner = isOwnerDept || isSuper;

  // Editable Status for Main Fields (B Section)
  const canEditMain = !readOnly && isOwner && (
      card.status === 'DRAFT' || 
      card.status === 'RETURNED' || 
      card.status === 'NOT_STARTED'
  );

  // Form State
  const [formA, setFormA] = useState(card.sectionA);
  const [formB, setFormB] = useState(card.sectionB);
  const [coAssistList, setCoAssistList] = useState<CoAssistSection[]>(card.coAssistSections || []);
  
  const [pendingFilesB, setPendingFilesB] = useState<File[]>([]);
  // Store pending files for each co-assist section by ID
  const [pendingFilesCoAssist, setPendingFilesCoAssist] = useState<Record<string, File[]>>({});

  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);

  const safeText = (v: any) => (typeof v === "string" ? v.trim() : "");

  // --- Regulatory Intel Manual Review (SuperUser) ---
  const [reviewStatus, setReviewStatus] = useState<string>(safeText((card as any)?.sectionA?.statusManual || ""));
  const [reviewRisk, setReviewRisk] = useState<string>(safeText((card as any)?.sectionA?.riskLevelManual || ""));
  const [reviewReason, setReviewReason] = useState<string>("");
  const [reviewing, setReviewing] = useState(false);

  const intelStatusAuto = safeText((card as any)?.sectionA?.statusAuto || "") || "S0_UNKNOWN";
  const intelRiskAuto = safeText((card as any)?.sectionA?.riskLevelAuto || "");

  const finalIntelStatus = (safeText((card as any)?.sectionA?.statusManual || "") || intelStatusAuto) as any;
  const finalIntelRisk = (safeText((card as any)?.sectionA?.riskLevelManual || "") || intelRiskAuto || card.riskLevel) as any;

  const intelStatusLabel = (s: string) => {
    const map: Record<string, string> = {
      S0_UNKNOWN: "未知/待判斷",
      S1_DRAFT_FOR_COMMENT: "草案/徵詢意見",
      S2_ANNOUNCED_NOT_EFFECTIVE: "已公告未生效",
      S3_EFFECTIVE_OBLIGATION: "已生效=公司義務",
      S4_SUPERSEDED: "已失效/被取代",
      S5_NOT_APPLICABLE: "不適用",
    };
    return map[s] || s || "—";
  };

  const docTypeLabel = (t: string) => {
    const map: Record<string, string> = {
      PREVIEW: "預告/草案",
      ANNOUNCEMENT: "公告/發布",
      ORDER: "令/解釋令",
      INTERPRETATION: "函釋/釋示/Q&A",
      REGULATION_TEXT: "法規條文",
      NEWS: "新聞稿/活動",
      OTHER: "其他",
    };
    return map[t] || t || "—";
  };

  const computeEligibilityLocal = () => {
    const status = finalIntelStatus as string;
    const effectiveDate = safeText((card as any)?.sectionA?.effectiveDate || "");
    const obligations = Array.isArray((card as any)?.sectionA?.obligationCandidates) ? (card as any).sectionA.obligationCandidates.filter(Boolean) : [];

    if (status === "S3_EFFECTIVE_OBLIGATION") {
      if (obligations.length === 0) return { ok: false, reason: "未解析到明確義務句（請人工補充後再轉）", reasonCode: "NO_OBLIGATION_CANDIDATES" };
      return { ok: true, mode: "CONVERT" };
    }

    if (status === "S2_ANNOUNCED_NOT_EFFECTIVE") {
      if (effectiveDate) {
        const d = new Date(effectiveDate + "T00:00:00Z");
        const now = new Date();
        const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
        const days = Math.ceil((d.getTime() - today.getTime()) / 86400000);
        if (days >= 0 && days <= 30) return { ok: true, mode: "PREPARE" };
      }
      return { ok: false, reason: "已公告但未生效（或生效日未知）", reasonCode: "NOT_EFFECTIVE_YET" };
    }

    if (status === "S1_DRAFT_FOR_COMMENT") return { ok: false, reason: "草案/徵詢階段，不開放轉專案", reasonCode: "DRAFT_FOR_COMMENT" };
    if (status === "S4_SUPERSEDED") return { ok: false, reason: "已失效/被取代", reasonCode: "SUPERSEDED" };
    if (status === "S5_NOT_APPLICABLE") return { ok: false, reason: "不適用", reasonCode: "NOT_APPLICABLE" };
    return { ok: false, reason: "狀態未知", reasonCode: "UNKNOWN" };
  };

  const convertEligibility = ((card as any)?.sectionA?.convertEligibility as any) || computeEligibilityLocal();

  const handleApplyReview = async (clear: boolean) => {
    if (!isSuper) return;
    const reason = reviewReason.trim();
    if (!reason) {
      showToast("請填覆核理由（必填）", "error");
      return;
    }
    setReviewing(true);
    try {
      await setRegIntelManualReview(
        card.id,
        {
          statusManual: clear ? null : (reviewStatus || null),
          riskLevelManual: clear ? null : (reviewRisk || null) as any,
          reason,
          clear,
        },
        user
      );
      showToast(clear ? "已清除人工覆核" : "已套用人工覆核");
      setReviewReason("");
      onRefresh();
    } catch (e: any) {
      console.error(e);
      showToast("覆核更新失敗：" + (e?.message || String(e)), "error");
    } finally {
      setReviewing(false);
    }
  };
  
  // Add Co-Assist Modal State
  const [showAddCoAssist, setShowAddCoAssist] = useState(false);
  const [selectedCoAssistCode, setSelectedCoAssistCode] = useState("");

  const { flow } = useApprovalFlow("GOV_PROJECT", card.id);

  // Sync state when switching cards.
  // NOTE: parent lists may recreate `card` object references on rerender; if we depend on the whole
  // `card`, local edits (including AI-drafted text) can be reset unexpectedly.
  useEffect(() => {
      setFormA(card.sectionA);
      setFormB(card.sectionB);
      setCoAssistList(card.coAssistSections || []);
      setReviewStatus(safeText((card as any)?.sectionA?.statusManual || ""));
      setReviewRisk(safeText((card as any)?.sectionA?.riskLevelManual || ""));
      setReviewReason("");
  }, [card.id]);

  const handleSave = async () => {
      setSaving(true);
      try {
          // Upload Files for B
          let newAttsB = [...formB.attachments];
          if (pendingFilesB.length > 0) {
              const uploaded = await uploadGovProjectFiles(card.id, pendingFilesB);
              newAttsB = [...newAttsB, ...uploaded];
          }

          // Upload Files for Co-Assist
          const updatedCoAssist = await Promise.all(coAssistList.map(async (sec) => {
              const files = pendingFilesCoAssist[sec.id] || [];
              if (files.length > 0) {
                  const uploaded = await uploadGovProjectFiles(card.id, files);
                  return { ...sec, attachments: [...sec.attachments, ...uploaded] };
              }
              return sec;
          }));

          const updatedB = { ...formB, attachments: newAttsB };

          await updateComplianceCard(card.id, {
              sectionB: updatedB,
              ownerDept: updatedB.ownerDept, // Sync top-level owner
              coAssistSections: updatedCoAssist
          });
          
          setPendingFilesB([]);
          setPendingFilesCoAssist({});
          
          setFormB(updatedB);
          setCoAssistList(updatedCoAssist);
          
          showToast("儲存成功");
          onRefresh();
      } catch (e) {
          console.error(e);
          showToast("儲存失敗", "error");
      } finally {
          setSaving(false);
      }
  };

  const handleSubmit = async () => {
      if (!formB.ownerDept) return alert("請指定主責部門");

      if (!confirm("確定送出呈核？(進入簽核流程後無法編輯)")) return;
      
      setSaving(true);
      try {
          // Quick save before submit logic (simplified reuse of logic above)
          let newAttsB = [...formB.attachments];
          if (pendingFilesB.length > 0) newAttsB = [...newAttsB, ...(await uploadGovProjectFiles(card.id, pendingFilesB))];
          
          const updatedCoAssist = await Promise.all(coAssistList.map(async (sec) => {
              const files = pendingFilesCoAssist[sec.id] || [];
              if (files.length > 0) return { ...sec, attachments: [...sec.attachments, ...(await uploadGovProjectFiles(card.id, files))] };
              return sec;
          }));

          const updatedB = { ...formB, attachments: newAttsB };
          const fullCard = { 
              ...card, 
              sectionB: updatedB, 
              coAssistSections: updatedCoAssist,
              ownerDept: updatedB.ownerDept
          };

          await updateComplianceCard(card.id, {
              sectionB: updatedB, 
              coAssistSections: updatedCoAssist,
              ownerDept: updatedB.ownerDept
          });

          await submitCardForApproval(fullCard, user);
          
          showToast("已送出，簽核流程啟動");
          onRefresh();
      } catch (e: any) {
          showToast("送出失敗: " + e.message, "error");
      } finally {
          setSaving(false);
      }
  };

  // --- Co-Assist Handlers ---

  const openAddCoAssist = () => {
      setSelectedCoAssistCode("");
      setShowAddCoAssist(true);
  };

  const confirmAddCoAssist = () => {
      if (!selectedCoAssistCode) {
          alert("請選擇部門");
          return;
      }
      
      const deptName = getDeptNameByCode(selectedCoAssistCode);
      const newSec: CoAssistSection = {
          id: `co_${Date.now()}`,
          deptCode: selectedCoAssistCode,
          deptName: deptName,
          content: "",
          attachments: []
      };
      setCoAssistList([...coAssistList, newSec]);
      setShowAddCoAssist(false);
  };

  const handleRemoveCoAssist = (index: number) => {
      if (!confirm("確定移除此協辦單位？既有內容將一併刪除。")) return;
      const next = [...coAssistList];
      next.splice(index, 1);
      setCoAssistList(next);
  };

  const updateCoAssist = (index: number, field: keyof CoAssistSection, val: any) => {
      const next = [...coAssistList];
      next[index] = { ...next[index], [field]: val };
      setCoAssistList(next);
  };

  
const handleAiDraft = async (targetIndex: number | 'owner') => {
    setAiLoading(true);
    try {
        const contextText = `
          【公告資訊】
          標題：${formA.title}
          主旨：${formA.subject}
          摘要：${formA.summary}
          法源：${(formA.legalBasis || []).join(', ')}
        `;

        let rolePrompt = "";
        let existingContent = "";
        let storedAttachments: AttachmentMeta[] = [];
        let localFiles: File[] = [];

        if (targetIndex === 'owner') {
            rolePrompt = "請根據公告資訊與附件內容，為「主責單位」擬定具體的執行計畫與應辦事項草稿。";
            existingContent = formB.content;
            storedAttachments = formB.attachments || [];
            localFiles = pendingFilesB || [];
        } else {
            const sec = coAssistList[targetIndex];
            rolePrompt = `請根據公告資訊、附件內容與主責單位的擬辦方向，為協辦單位「${sec.deptName}」擬定配合事項與建議。`;
            existingContent = sec.content;
            storedAttachments = sec.attachments || [];
            localFiles = (pendingFilesCoAssist[sec.id] || []);
        }

        const instruction = [
          rolePrompt,
          "",
          contextText,
          "",
          "⚠️ 重要：請以「實際附件內容」為主，不要只依卡片標題/主題推測。",
          "輸出請分兩段：",
          "1) 務實法務版：白話摘要、風險點、需要補件/修訂清單、進攻談判建議（如適用）。",
          "2) 管理報告版：3–7點重點摘要、風險等級（高/中/低與理由）、需要主管決策的點與建議方向。",
          "最後加上 5 行以內『下一步』。",
        ].join("\n");

        const ownerReference = safeText(formB.content);
        const finalInstruction = instruction + "\n\n(主責擬辦參考: " + ownerReference + ")";

        let aiText = "";

        // 優先：使用本次上傳的原始檔案（未存檔前也能分析）
        if (Array.isArray(localFiles) && localFiles.length > 0) {
            const mode: any = localFiles.length >= 2 ? "batch_analysis" : "analysis";
            const res: any = await analyzeUploadedFile({
                userInstruction: finalInstruction,
                mode,
                module: "gov_project",
                currentFields: { cardId: card.id, ownerDept: formB.ownerDept, target: targetIndex === 'owner' ? 'owner' : 'coassist' },
                files: localFiles
            } as any);

            if (!res?.ok) throw new Error(res?.message || "AI 生成失敗");
            aiText = safeText(res.summary);
        } 
        // 次選：使用已儲存的附件 downloadUrl（可一次帶多個附件）
        else if (Array.isArray(storedAttachments) && storedAttachments.length > 0) {
            const atts = storedAttachments
                .map((a: any) => {
                    const url = safeText(a?.url || a?.downloadUrl || a?.downloadURL);
                    if (!url) return null;
                    return {
                        name: a.name || "attachment",
                        mimeType: safeText((a as any).contentType || (a as any).mimeType || ""),
                        downloadUrl: url
                    };
                })
                .filter(Boolean) as any[];

            const r = await callLegalAssistant({
                mode: "analyze_document",
                userInstruction: finalInstruction,
                currentFields: { cardId: card.id, ownerDept: formB.ownerDept, target: targetIndex === 'owner' ? 'owner' : 'coassist' },
                attachments: atts
            } as any);

            if (!r?.ok) throw new Error(r?.message || "AI 生成失敗");
            aiText = safeText(r.text);
        } else {
            showToast("請先上傳附件後再使用 AI 產生草稿", "error");
            return;
        }

        const newText = ((safeText(existingContent) ? safeText(existingContent) + "\n\n" : "") + aiText).trim();

        if (targetIndex === 'owner') {
            setFormB({ ...formB, content: newText });
        } else {
            updateCoAssist(targetIndex, 'content', newText);
        }

        showToast("AI 草稿已產生並回填");
    } catch (e: any) {
        console.error(e);
        showToast("AI 生成失敗: " + (e?.message || "未知錯誤"), "error");
    } finally {
        setAiLoading(false);
    }
};

const handleDecision = async (stageKey: string, decision: any, comment: string) => {
      if (!flow) return;
      
      // Update persistent rejection log if rejected/returned
      if (decision === 'REJECTED' || decision === 'RETURNED') {
          const entry: RejectionLogEntry = {
              flowId: flow.id,
              stepKey: stageKey,
              approverEmail: user?.email || '',
              approverName: user?.displayName || 'Approver',
              rejectedAt: new Date().toISOString(),
              comment: comment
          };
          
          await updateComplianceCard(card.id, { 
              status: 'RETURNED',
              rejectionLog: [...(card.rejectionLog || []), entry] 
          });
          showToast("已退回，主責單位可重新編輯");
      }
      
      await updateStageDecision({
          flowId: flow.id,
          stageKey: stageKey as any,
          decision,
          comment,
          actorUid: user?.id || '',
          actorName: user?.displayName || 'User'
      });
      
      if (decision === 'APPROVED') {
           showToast("審核完成");
      }
      onRefresh(); 
  };
  
  // Filter for Co-Assist Options (Exclude Owner and Existing Co-Assist)
  const availableCoDepts = DEPARTMENTS.filter(d => {
      // Normalize to Code comparison
      const ownerCode = DEPARTMENTS.find(dep => dep.name === formB.ownerDept || dep.code === formB.ownerDept)?.code || formB.ownerDept;
      if (d.code === ownerCode) return false;
      if (coAssistList.some(c => c.deptCode === d.code)) return false;
      return true;
  });

  // Display value for Owner Dept
  const ownerDeptDisplay = getDeptNameByCode(DEPARTMENTS.find(d => d.name === formB.ownerDept || d.code === formB.ownerDept)?.code || formB.ownerDept);

  return (
    <div style={{paddingBottom: 40}}>
          
      {/* SECTION A: Read Only */}
      <div className="section" style={{marginBottom: 20, background: '#1a1d21', border: '1px solid #333', borderRadius: 8, padding: 16}}>
          <div style={{fontSize: 14, color: '#93c5fd', fontWeight: 'bold', borderBottom: '1px solid #444', paddingBottom: 8, marginBottom: 12}}>
              A. 主管機關公告 / 情資判斷（唯讀）
          </div>

          <div className="grid" style={{gridTemplateColumns: '1fr 1fr 1fr', gap: 12}}>
              <div className="field">
                  <label className="label">主管機關</label>
                  <div className="p" style={{background:'#111', padding:8, borderRadius:4}}>{(formA as any).sourceAgency || (card as any).agencyName || "—"}</div>
              </div>
              <div className="field">
                  <label className="label">文件類型</label>
                  <div className="p" style={{background:'#111', padding:8, borderRadius:4}}>{docTypeLabel(safeText((formA as any).docType || ""))}</div>
              </div>
              <div className="field">
                  <label className="label">來源網站</label>
                  <div className="p" style={{background:'#111', padding:8, borderRadius:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>{safeText((formA as any).sourceSite || "") || "—"}</div>
              </div>

              <div className="field">
                  <label className="label">發布日</label>
                  <div className="p" style={{background:'#111', padding:8, borderRadius:4}}>{safeText((formA as any).publishDate || formA.dateText || formA.date || "") || "—"}</div>
              </div>
              <div className="field">
                  <label className="label">生效日</label>
                  <div className="p" style={{background:'#111', padding:8, borderRadius:4}}>{safeText((formA as any).effectiveDate || "") || "—"}</div>
              </div>
              <div className="field">
                  <label className="label">徵詢截止日</label>
                  <div className="p" style={{background:'#111', padding:8, borderRadius:4}}>{safeText((formA as any).commentDeadline || "") || "—"}</div>
              </div>

              <div className="field" style={{gridColumn: '1/-1'}}>
                  <label className="label">專案/公告標題</label>
                  <div className="p" style={{background:'#111', padding:8, borderRadius:4}}>{formA.title}</div>
              </div>

              <div className="field" style={{gridColumn: '1/-1'}}>
                  <label className="label">公告連結</label>
                  <div style={{display:'flex', gap: 8, alignItems:'center'}}>
                      <div className="p" style={{flex:1, background:'#111', padding:8, borderRadius:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                          {formA.announcementUrl || formA.sourceUrl || "—"}
                      </div>
                      {(formA.announcementUrl || formA.sourceUrl) && (
                          <a 
                              href={formA.announcementUrl || formA.sourceUrl} 
                              target="_blank" 
                              rel="noreferrer"
                              className="btn-outline-white"
                              style={{textDecoration:'none', padding:'6px 12px', fontSize:12, borderRadius:4, border:'1px solid rgba(255,255,255,0.3)'}}
                          >
                              前往 ↗
                          </a>
                      )}
                  </div>
              </div>

              <div className="field" style={{gridColumn: '1/-1'}}>
                  <label className="label">法源依據 / 主旨</label>
                  <div className="p" style={{background:'#111', padding:8, borderRadius:4, whiteSpace:'pre-wrap'}}>
                      {(
                        Array.isArray((formA as any).legalBasisRefs) && (formA as any).legalBasisRefs.length > 0
                          ? (formA as any).legalBasisRefs.join('、')
                          : (formA.subject || formA.legalBasis.join(', '))
                      ) || "—"}
                  </div>
              </div>

              <div className="field" style={{gridColumn: '1/-1'}}>
                  <label className="label">狀態 / 風險（Auto → Final）</label>
                  <div className="p" style={{background:'#111', padding:12, borderRadius:6, lineHeight:1.5}}>
                      <div style={{display:'flex', gap: 14, flexWrap:'wrap'}}>
                        <div><span style={{color:'#aaa'}}>Auto：</span>{intelStatusLabel(intelStatusAuto)} / {intelRiskAuto || "—"}</div>
                        <div><span style={{color:'#aaa'}}>Final：</span>{intelStatusLabel(String(finalIntelStatus))} / {String(finalIntelRisk || "—")}</div>
                      </div>
                      <div style={{marginTop: 8, fontSize: 12, color: convertEligibility?.ok ? '#10b981' : '#f59e0b'}}>
                        {convertEligibility?.ok
                          ? `可轉專案：${convertEligibility.mode === 'PREPARE' ? '預備轉（≤30天生效）' : '立即轉（已生效義務）'}`
                          : `不可轉：${convertEligibility?.reason || convertEligibility?.reasonCode || '—'}`}
                      </div>
                  </div>
              </div>

              <div className="field" style={{gridColumn: '1/-1'}}>
                  <label className="label">疑似義務句（AI 抽取）</label>
                  <div className="p" style={{background:'#111', padding:12, borderRadius:6, minHeight: 44, whiteSpace:'pre-wrap', lineHeight:1.5}}>
                      {Array.isArray((formA as any).obligationCandidates) && (formA as any).obligationCandidates.length > 0 ? (
                        <ul style={{margin:0, paddingLeft:18}}>
                          {(formA as any).obligationCandidates.slice(0, 6).map((s: string, i: number) => (
                            <li key={i} style={{marginBottom: 6}}>{s}</li>
                          ))}
                        </ul>
                      ) : (
                        <span style={{color:'#666'}}>—</span>
                      )}
                  </div>
              </div>

              <div className="field" style={{gridColumn: '1/-1'}}>
                  <label className="label">判斷依據片段（最多 3 段）</label>
                  <div className="p" style={{background:'#111', padding:12, borderRadius:6, minHeight: 44, whiteSpace:'pre-wrap', lineHeight:1.5}}>
                      {Array.isArray((formA as any).evidenceSnippets) && (formA as any).evidenceSnippets.length > 0 ? (
                        <div style={{display:'flex', flexDirection:'column', gap: 10}}>
                          {(formA as any).evidenceSnippets.slice(0, 3).map((s: string, i: number) => (
                            <div key={i} style={{borderLeft:'3px solid rgba(255,255,255,0.15)', paddingLeft:10, color:'#ddd'}}>{s}</div>
                          ))}
                        </div>
                      ) : (
                        <span style={{color:'#666'}}>—</span>
                      )}
                  </div>
              </div>

              <div className="field" style={{gridColumn: '1/-1'}}>
                  <label className="label">摘要</label>
                  <div className="p" style={{background:'#111', padding:12, borderRadius:6, minHeight: 60, whiteSpace:'pre-wrap', lineHeight:1.5}}>
                      {formA.summary || "—"}
                  </div>
              </div>
          </div>

          <div style={{marginTop: 12}}>
             <div className="label">原始附檔連結 / 來源附件</div>
             {formA.attachmentLinks && formA.attachmentLinks.length > 0 ? (
                 <ul style={{margin:0, paddingLeft:20, fontSize:13}}>
                     {formA.attachmentLinks.map((l, i) => (
                         <li key={i}><a href={l} target="_blank" rel="noreferrer" style={{color:'#60a5fa'}}>{l}</a></li>
                     ))}
                 </ul>
             ) : <div className="p" style={{color:'#666'}}>無</div>}
          </div>

          {formA.attachments.length > 0 && (
              <div style={{marginTop: 12}}>
                  <div className="label">已歸檔附件</div>
                  <div style={{display:'flex', flexWrap:'wrap', gap:8}}>
                      {formA.attachments.map((f, i) => (
                          <a key={i} href={f.url} target="_blank" rel="noreferrer" style={{fontSize:12, color:'#60a5fa', textDecoration:'none'}}>📄 {f.name}</a>
                      ))}
                  </div>
              </div>
          )}

          {/* Manual Review Panel */}
          {!readOnly && card.type === 'intel' && isSuper && (
            <div style={{marginTop: 16, paddingTop: 14, borderTop: '1px dashed rgba(255,255,255,0.15)'}}>
              <div style={{fontSize: 13, color:'#fbbf24', fontWeight: 'bold', marginBottom: 10}}>人工覆核（manual 覆蓋 auto）</div>

              <div className="grid" style={{gridTemplateColumns: '1fr 1fr 2fr', gap: 12}}>
                <div className="field">
                  <label className="label">覆核狀態</label>
                  <select className="input" value={reviewStatus} onChange={(e) => setReviewStatus(e.target.value)} disabled={reviewing}>
                    <option value="">（不覆核／用 Auto）</option>
                    <option value="S0_UNKNOWN">S0 未知</option>
                    <option value="S1_DRAFT_FOR_COMMENT">S1 草案/徵詢</option>
                    <option value="S2_ANNOUNCED_NOT_EFFECTIVE">S2 已公告未生效</option>
                    <option value="S3_EFFECTIVE_OBLIGATION">S3 已生效義務</option>
                    <option value="S4_SUPERSEDED">S4 已失效/被取代</option>
                    <option value="S5_NOT_APPLICABLE">S5 不適用</option>
                  </select>
                </div>

                <div className="field">
                  <label className="label">覆核風險</label>
                  <select className="input" value={reviewRisk} onChange={(e) => setReviewRisk(e.target.value)} disabled={reviewing}>
                    <option value="">（不覆核／用 Auto）</option>
                    <option value="HIGH">HIGH 高</option>
                    <option value="MEDIUM">MEDIUM 中</option>
                    <option value="LOW">LOW 低</option>
                  </select>
                </div>

                <div className="field">
                  <label className="label">覆核理由（必填）</label>
                  <textarea
                    className="input"
                    rows={2}
                    value={reviewReason}
                    onChange={(e) => setReviewReason(e.target.value)}
                    placeholder="例如：此文件為正式公告且已即日生效；或：僅適用政府機關，不適用本公司..."
                    disabled={reviewing}
                  />
                </div>
              </div>

              <div style={{display:'flex', justifyContent:'flex-end', gap: 8, marginTop: 10}}>
                <button className="btn" style={{width:'auto', background:'#3b82f6', color:'#fff'}} onClick={() => handleApplyReview(false)} disabled={reviewing}>
                  {reviewing ? '更新中...' : '套用覆核'}
                </button>
                <button className="btn" style={{width:'auto', background:'#555'}} onClick={() => handleApplyReview(true)} disabled={reviewing}>
                  {reviewing ? '更新中...' : '清除覆核'}
                </button>
              </div>
            </div>
          )}
      </div>

      {/* SECTION B: OWNER */}
      <div className="section" style={{marginBottom: 20, background: '#222', border: '1px solid #333', borderRadius: 8, padding: 16}}>
           <div style={{display:'flex', justifyContent:'space-between', borderBottom: '1px solid #444', paddingBottom: 8, marginBottom: 12}}>
              <div style={{fontSize: 14, color: '#fdba74', fontWeight: 'bold'}}>B. 主責單位擬辦</div>
              {formB.ownerDeptSuggestionText && (
                  <div style={{fontSize: 11, color: '#fbbf24', background:'rgba(245, 158, 11, 0.1)', padding:'2px 6px', borderRadius:4}}>
                      建議: {formB.ownerDeptSuggestionText}
                  </div>
              )}
           </div>
           
           <div className="field">
               <label className="label">主責部門 <span style={{color:'red'}}>*</span></label>
               {isSuper ? (
                   <select 
                      className="input" 
                      value={DEPARTMENTS.find(d => d.name === formB.ownerDept || d.code === formB.ownerDept)?.code || formB.ownerDept} 
                      onChange={e => setFormB({...formB, ownerDept: e.target.value})}
                      disabled={!canEditMain}
                   >
                       <option value="">請選擇</option>
                       {DEPARTMENTS.map(d => <option key={d.code} value={d.code}>{d.name}</option>)}
                   </select>
               ) : (
                   <div className="input" style={{background:'#111', color: formB.ownerDept ? '#fff' : '#666'}}>
                       {ownerDeptDisplay || "待指定 (請聯絡管理員)"}
                   </div>
               )}
           </div>
           
           <div className="field" style={{marginTop: 12}}>
               <AiFieldHeader 
                   label="擬辦內容" 
                   loading={aiLoading} 
                   onClick={() => handleAiDraft('owner')} 
                   disabled={!canEditMain}
               />
               <textarea 
                  className="input" rows={4} 
                  value={formB.content} 
                  onChange={e => setFormB({...formB, content: e.target.value})}
                  disabled={!canEditMain}
                  placeholder="請輸入具體執行計畫、時程與負責人員..."
               />
           </div>
           <div style={{marginTop: 12}}>
               <FileUploadKit 
                   label="附件上傳 (B段)"
                   existingFiles={formB.attachments}
                   rawFiles={pendingFilesB}
                   onFilesSelected={files => setPendingFilesB(prev => [...prev, ...files])}
                   onRemoveRawFile={idx => setPendingFilesB(prev => prev.filter((_, i) => i !== idx))}
                   onRemoveFile={(f, idx) => {
                       const next = [...formB.attachments];
                       next.splice(idx, 1);
                       setFormB({...formB, attachments: next});
                   }}
                   disabled={!canEditMain}
               />
           </div>
      </div>

      {/* SECTION C: Multi Co-Assist */}
      <div className="section" style={{marginBottom: 20, background: '#222', border: '1px solid #333', borderRadius: 8, padding: 16}}>
           <div style={{display:'flex', justifyContent:'space-between', borderBottom: '1px solid #444', paddingBottom: 8, marginBottom: 12}}>
              <div style={{fontSize: 14, color: '#d8b4fe', fontWeight: 'bold'}}>C. 協辦單位 ({coAssistList.length})</div>
              {isSuper && (
                  <button className="smallbtn" onClick={openAddCoAssist}>+ 新增協辦</button>
              )}
           </div>
           
           <div style={{display:'flex', flexDirection:'column', gap: 20}}>
               {coAssistList.map((sec, idx) => {
                   // Permission: SuperUser OR User is in this Dept
                   const canEditSec = isSuper || (userDeptCode === sec.deptCode);
                   
                   return (
                       <div key={sec.id} style={{padding: 12, border: '1px dashed #444', borderRadius: 6, background: 'rgba(255,255,255,0.02)'}}>
                           <div style={{display:'flex', justifyContent:'space-between', marginBottom: 8}}>
                               <div style={{fontWeight:'bold', color:'#e9d5ff'}}>{getDeptNameByCode(sec.deptCode)} 協辦</div>
                               {isSuper && <button className="smallbtn" style={{color:'var(--danger)', borderColor:'var(--danger)', fontSize:10, padding:'2px 6px'}} onClick={() => handleRemoveCoAssist(idx)}>移除</button>}
                           </div>
                           
                           <AiFieldHeader 
                               label="協辦內容" 
                               loading={aiLoading} 
                               onClick={() => handleAiDraft(idx)} 
                               disabled={!canEditSec}
                           />
                           <textarea 
                              className="input" rows={3} 
                              value={sec.content} 
                              onChange={e => updateCoAssist(idx, 'content', e.target.value)}
                              disabled={!canEditSec}
                              placeholder="請輸入協辦事項..."
                           />
                           
                           <div style={{marginTop: 8}}>
                               <FileUploadKit 
                                   label="附件"
                                   existingFiles={sec.attachments}
                                   rawFiles={pendingFilesCoAssist[sec.id] || []}
                                   onFilesSelected={files => setPendingFilesCoAssist({...pendingFilesCoAssist, [sec.id]: [...(pendingFilesCoAssist[sec.id]||[]), ...files]})}
                                   onRemoveRawFile={(fIdx) => {
                                       const list = pendingFilesCoAssist[sec.id] || [];
                                       setPendingFilesCoAssist({...pendingFilesCoAssist, [sec.id]: list.filter((_, i) => i !== fIdx)});
                                   }}
                                   onRemoveFile={(f, fIdx) => {
                                       const nextAtts = [...sec.attachments];
                                       nextAtts.splice(fIdx, 1);
                                       updateCoAssist(idx, 'attachments', nextAtts);
                                   }}
                                   disabled={!canEditSec}
                               />
                           </div>
                       </div>
                   );
               })}
               {coAssistList.length === 0 && <div className="p" style={{textAlign:'center', padding:10}}>無協辦單位</div>}
           </div>
      </div>

      {/* REJECTION LOG (Immutable History) */}
      {card.rejectionLog && card.rejectionLog.length > 0 && (
          <div className="section" style={{marginBottom: 20, padding: 12, border: '1px solid #ef4444', background: 'rgba(239, 68, 68, 0.05)', borderRadius: 8}}>
              <div style={{color: '#ef4444', fontWeight:'bold', fontSize:13, marginBottom: 8}}>⚠️ 歷次駁回紀錄</div>
              <div style={{display:'flex', flexDirection:'column', gap: 8}}>
                  {card.rejectionLog.map((log, i) => (
                      <div key={i} style={{fontSize:13, color:'#ddd', borderBottom: i < card.rejectionLog!.length-1 ? '1px dashed rgba(255,255,255,0.1)' : 'none', paddingBottom: 6}}>
                          <span style={{color:'#aaa', marginRight: 8}}>[{new Date(log.rejectedAt).toLocaleString()}]</span>
                          <span style={{fontWeight:'bold', marginRight: 8}}>{log.approverName}:</span>
                          <span>{log.comment}</span>
                      </div>
                  ))}
              </div>
          </div>
      )}

      {/* SECTION D: FLOW */}
      {card.status !== 'DRAFT' && card.status !== 'NOT_STARTED' && flow && (
          <div style={{marginTop: 20, borderTop: '1px solid #444', paddingTop: 16}}>
              <div style={{fontSize: 14, color: '#ccc', marginBottom: 12}}>D. 簽核流程</div>
              <ApprovalFlowPanel flow={flow} onDecision={handleDecision} />
          </div>
      )}

      {/* ACTIONS */}
      <div style={{marginTop: 24, display: 'flex', gap: 12, justifyContent: 'flex-end', borderTop: '1px solid #333', paddingTop: 16}}>
           
           {canEditMain && (
               <>
                   <button className="btn" style={{width:'auto', background:'#333'}} onClick={handleSave} disabled={saving}>儲存</button>
                   <button className="btn" style={{width:'auto', background:'#3b82f6', color:'#fff'}} onClick={handleSubmit} disabled={saving}>送出呈核</button>
               </>
           )}
           
           {!readOnly && card.type === 'intel' && isSuper && (
               <>
                   {(() => {
                      const ok = Boolean((convertEligibility as any)?.ok);
                      const mode = (convertEligibility as any)?.mode;
                      const reason = safeText((convertEligibility as any)?.reason || (convertEligibility as any)?.reasonCode || "");
                      const label = ok
                        ? (mode === 'PREPARE' ? '預備轉專案（公司義務）' : '一鍵轉公司義務（專案）')
                        : '不可轉專案';

                      const tip = ok
                        ? (mode === 'PREPARE' ? '已公告未生效，但生效日在 30 天內：可先建立「準備中」專案' : '已生效且構成公司義務：可直接轉為專案控管')
                        : (reason || '目前狀態/資料不足，請先人工覆核或補齊義務句');

                      const onConvert = async () => {
                        if (!ok) {
                          showToast(tip, 'error');
                          return;
                        }
                        const msg = mode === 'PREPARE'
                          ? '確定要建立「預備公司義務」專案嗎？（可後續再調整主責/期限/任務）'
                          : '確定要將此情資「一鍵轉為公司義務專案」嗎？';
                        if (!confirm(msg)) return;
                        await moveIntelToProject(card, user);
                        onRefresh();
                      };

                      return (
                        <button
                          className="btn"
                          style={{width:'auto', background: ok ? '#10b981' : '#444', color:'#fff', opacity: ok ? 1 : 0.7, cursor: ok ? 'pointer' : 'not-allowed'}}
                          onClick={onConvert}
                          disabled={!ok}
                          title={tip}
                        >
                          {label}
                        </button>
                      );
                   })()}
                   <button className="btn" style={{width:'auto', background:'#666'}} onClick={() => archiveCard(card.id, user).then(onRefresh)}>知悉歸檔</button>
               </>
           )}
      </div>

      {/* Add Co-Assist Modal */}
      {showAddCoAssist && (
          <Modal title="新增協辦單位" onClose={() => setShowAddCoAssist(false)} width={400}>
              <div style={{padding: '10px 0'}}>
                  <label className="label">選擇部門</label>
                  <select 
                      className="input" 
                      value={selectedCoAssistCode} 
                      onChange={e => setSelectedCoAssistCode(e.target.value)}
                  >
                      <option value="" disabled>請選擇</option>
                      {availableCoDepts.map(d => (
                          <option key={d.code} value={d.code}>{d.name}</option>
                      ))}
                  </select>
                  {availableCoDepts.length === 0 && <div className="p" style={{marginTop:8, color:'orange'}}>無其他可用部門</div>}
              </div>
              <div className="row" style={{marginTop: 20, justifyContent: 'flex-end'}}>
                  <button className="btn" style={{width:'auto'}} onClick={confirmAddCoAssist}>新增</button>
              </div>
          </Modal>
      )}
    </div>
  );
};
